import { useState } from 'react';

import { Button } from '@components/button';
import { CodeBlock } from '@components/code-block';
import { createForm, useCreateFormStore } from '@components/form';
import { IconStatusIndicator } from '@components/icons/status-indicator';
import { Modal, hideModal, useModal } from '@components/modal';
import { Stack } from '@components/stack';
import { StatusOptionLabel } from '@components/status-option-label';
import { Text } from '@components/text';
import { useToaster } from '@components/toaster';
import { useScopedTranslate } from '@locale/translate';

import { endpointColorMap } from '../common';
import { useUnsubscribeIntegrationWebhook } from '../integrations-hooks';
import {
  EndpointMethod,
  endpointMethods,
  IntegrationWebhookCreateRemoveType,
  IntegrationWebhookCreateRequestType,
  type IntegrationWebhook,
} from '../integrations-types';

export const useUnsubscribeWebhookModal = () => useModal(UnsubscribeWebhook, { variant: 'LARGE' });

const Form = createForm<IntegrationWebhookCreateRemoveType>({ space: 0 });

const UnsubscribeWebhook = ({ webhook }: { webhook: IntegrationWebhook }) => {
  const { t } = useScopedTranslate(locale);
  const { showSuccess } = useToaster();
  const formStore = useCreateFormStore();
  const requestMethod = formStore.useFieldValue('method') as EndpointMethod;
  const allowedMethods = endpointMethods.filter((method) => method !== 'GET');
  const unsubscribeMutation = useUnsubscribeIntegrationWebhook(webhook.shortId);
  const [requestResponse, setRequestResponse] = useState<
    IntegrationWebhookCreateRequestType | undefined
  >(undefined);

  const handleRemove = async (data: IntegrationWebhookCreateRemoveType) => {
    const response = await unsubscribeMutation.mutateAsync(data);
    if (response.success) {
      showSuccess(t('webhookHasBeenUnsubscribed'));
      hideModal();
    } else {
      setRequestResponse(response);
    }
  };

  if (requestResponse) {
    return (
      <Modal>
        <Modal.Header>
          <Modal.HeaderTitle title={t('unsubscribeWebhook')} />
          <Stack orientation="horizontal" align="center">
            <IconStatusIndicator color="elementRed100" />
            <Text
              font="regular14"
              color="elementRed100"
              text={t('fail', requestResponse.statusCode.toString())}
            />
          </Stack>
        </Modal.Header>
        <Modal.Content>
          <CodeBlock header={t('response')} data={requestResponse.data} />
        </Modal.Content>
        <Modal.Footer>
          <Stack orientation="horizontal" justify="flex-end">
            <Button text={t('core.close')} variant="secondary" onClick={hideModal} />
            <Button
              text={t('tryAgain')}
              variant="primary-blue"
              onClick={() => setRequestResponse(undefined)}
            />
          </Stack>
        </Modal.Footer>
      </Modal>
    );
  }

  return (
    <Modal>
      <Form store={formStore} onSubmit={handleRemove}>
        <Modal.SimpleHeader title={t('unsubscribeWebhook')} />
        <Modal.Content>
          <Form.TextField id="url" label={t('url')} validators={['valid-http-url', 'required']} />
          <Form.Select
            id="method"
            label={t('method')}
            validators={['required']}
            options={allowedMethods.map((endpointMethod) => ({
              label: (
                <StatusOptionLabel text={endpointMethod} color={endpointColorMap[endpointMethod]} />
              ),
              value: endpointMethod,
            }))}
            isClearable={false}
            defaultValue="DELETE"
          />
          {requestMethod !== 'DELETE' && <Form.JsonEditor id="body" label={t('requestValues')} />}
        </Modal.Content>
        <Modal.Footer>
          <Form.Actions>
            <Form.Reset text={t('core.cancel')} variant="secondary" onClick={hideModal} />
            <Form.Submit
              variant="primary-blue"
              text={t('unsubsribe')}
              disabled={unsubscribeMutation.isLoading}
            />
          </Form.Actions>
        </Modal.Footer>
      </Form>
    </Modal>
  );
};

const locale = {
  en: {
    unsubscribeWebhook: 'Unsubscribe from a Webhook',
    url: 'Url',
    method: 'Method',
    requestValues: 'Request values',
    fail: 'Fail • {0}',
    response: 'RESPONSE',
    tryAgain: 'Try again',
    webhookHasBeenUnsubscribed: 'Webhook has been unsubscribed',
    unsubsribe: 'Unsubscribe',
  },
};
